<script setup>
import { ref } from "vue";
import { formatMoney } from "../../utils/analyticsUtils.js";

defineProps({
  daily: { type: Array, default: () => [] },
  monthly: { type: Array, default: () => [] },
  yearly: { type: Array, default: () => [] },
  maxDaily: { type: Number, default: 1 },
  maxMonthly: { type: Number, default: 1 },
  maxYearly: { type: Number, default: 1 },
});

// Tooltip State
const hoveredPoint = ref(null);
const tooltipStyle = ref({});

function showTooltip(e, label, value) {
  const rect = e.target.getBoundingClientRect();
  hoveredPoint.value = {
    label: label,
    value: formatMoney(value),
  };
  tooltipStyle.value = {
    top: `${rect.top - 40}px`,
    left: `${rect.left + rect.width / 2}px`,
  };
}

function hideTooltip() {
  hoveredPoint.value = null;
}
</script>

<template>
  <section class="panel sales-series">
    <div class="panel-head">
      <b>Ventas por día</b>
      <div class="panel-sub">{{ daily.length }} días</div>
    </div>
    <div class="flow-bars">
      <div v-for="d in daily" :key="d.date" class="flow-col">
        <div class="flow-pos" :style="{ height: (Number(d.total) / maxDaily) * 120 + 'px' }"
          @mouseenter="showTooltip($event, new Date(d.date).toLocaleDateString(), Number(d.total))"
          @mouseleave="hideTooltip"></div>
        <div class="flow-label">{{ new Date(d.date).getDate() }}</div>
      </div>
    </div>
  </section>

  <section class="panel sales-series">
    <div class="panel-head">
      <b>Ventas por mes</b>
      <div class="panel-sub">últimos 12 meses</div>
    </div>
    <div class="bars">
      <div v-for="m in monthly" :key="m.key" class="bars-col">
        <div class="bar-single" :style="{
          height: (Number(m.total) / maxMonthly) * 160 + 'px',
        }" @mouseenter="showTooltip($event, m.label, Number(m.total))" @mouseleave="hideTooltip"></div>
        <div class="bars-label">{{ m.label }}</div>
      </div>
    </div>
  </section>

  <section class="panel sales-series">
    <div class="panel-head">
      <b>Ventas por año</b>
      <div class="panel-sub">últimos 5 años</div>
    </div>
    <div class="bars">
      <div v-for="y in yearly" :key="y.year" class="bars-col">
        <div class="bar-single" :style="{ height: (Number(y.total) / maxYearly) * 160 + 'px' }"
          @mouseenter="showTooltip($event, y.year, Number(y.total))" @mouseleave="hideTooltip"></div>
        <div class="bars-label">{{ y.year }}</div>
      </div>
    </div>
  </section>

  <!-- Local Tooltip Portal -->
  <Teleport to="body">
    <div v-if="hoveredPoint" class="chart-tooltip" :style="tooltipStyle">
      <div class="tt-label">{{ hoveredPoint.label }}</div>
      <div class="tt-val">{{ hoveredPoint.value }}</div>
    </div>
  </Teleport>
</template>

<style scoped>
.panel {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: var(--text);
}

.panel-sub {
  color: var(--accent);
  font-weight: 700;
}

.sales-series {
  margin-top: 0.6rem;
}

.flow-bars {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12px, 1fr));
  align-items: end;
  gap: 0.35rem;
  padding: 0.6rem 0.4rem;
}

.flow-col {
  display: grid;
  grid-template-rows: auto auto auto;
  align-items: end;
  justify-items: center;
  gap: 0.2rem;
}

.flow-pos {
  width: 16px;
  background: linear-gradient(180deg, #6a5cf5, #00c2ff);
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.flow-pos:hover {
  opacity: 0.8;
}

.flow-label {
  font-size: 0.75rem;
  color: var(--muted);
}

.bars {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
  align-items: end;
  gap: 0.8rem;
  min-height: 220px;
}

.bars-col {
  display: grid;
  align-items: end;
  justify-items: center;
  gap: 0.35rem;
}

.bar-single {
  width: 60%;
  background: linear-gradient(180deg, #6a5cf5, #00c2ff);
  transition: height 0.28s ease, opacity 0.2s;
  cursor: pointer;
}

.bar-single:hover {
  opacity: 0.8;
}

.bars-label {
  color: var(--muted);
  font-size: 0.9rem;
}

/* Tooltip Styles (Global/Teleported) */
.chart-tooltip {
  position: fixed;
  transform: translate(-50%, -100%);
  background: rgba(15, 23, 42, 0.9);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
}

.tt-label {
  font-weight: 600;
  margin-bottom: 2px;
  color: #cbd5e1;
}

.tt-val {
  font-weight: 700;
  color: #fff;
}

/* Responsive Design */
@media (max-width: 768px) {
  .panel {
    padding: 0.85rem;
  }

  .panel-head {
    font-size: 0.9rem;
  }

  .panel-sub {
    font-size: 0.8rem;
  }

  .flow-bars {
    padding: 0.4rem 0.2rem;
    gap: 0.25rem;
  }

  .flow-pos {
    width: 14px;
  }

  .flow-label {
    font-size: 0.7rem;
  }

  .bars {
    gap: 0.6rem;
    min-height: 180px;
    grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  }

  .bar-single {
    width: 55%;
  }

  .bars-label {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .panel {
    padding: 0.75rem;
  }

  .panel-head {
    font-size: 0.85rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .panel-sub {
    font-size: 0.75rem;
  }

  .sales-series {
    margin-top: 0.4rem;
  }

  .flow-bars {
    padding: 0.3rem 0.1rem;
    gap: 0.2rem;
  }

  .flow-pos {
    width: 12px;
  }

  .flow-label {
    font-size: 0.65rem;
  }

  .bars {
    gap: 0.5rem;
    min-height: 150px;
    grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
  }

  .bar-single {
    width: 50%;
  }

  .bars-label {
    font-size: 0.8rem;
  }
}
</style>
